/**
 * Application principale pour le restaurant L'Ancre
 * Gestion des animations, validation formulaire, carte et restrictions de dates
 */

'use strict';

// ========================================
// Configuration et constantes
// ========================================

const CONFIG = window.CONFIG || {
    recaptchaSiteKey: '',
    joursFermes: { weekly: [1], dates: [] },
    csrfToken: ''
};

const SELECTORS = {
    navbar: '#navbar',
    heroScrollIndicator: '.hero__scroll-indicator',
    menuButtons: '[data-menu]',
    reservationForm: '.reservation-form',
    dateInput: '#date',
    periodeSelect: '#periode',
    heureSelect: '#heure',
    navbarToggle: '.navbar__toggle',
    navbarMenu: '.navbar__menu',
    revealElements: '.reveal, .section'
};

const URLS = {
    menuFr: 'pdfs/menu-fr.html',
    menuEn: 'pdfs/menu-en.html'
};

const TRANSLATIONS = {
    fr: {
        'Formule déjeuner': 'Formule déjeuner',
        'Menu gastronomique': 'Menu gastronomique',
        'Voir le menu complet': 'Voir le menu complet',
        'Réserver maintenant': 'Réserver maintenant'
    },
    en: {
        'Formule déjeuner': 'Lunch menu',
        'Menu gastronomique': 'Gastronomic menu',
        'Voir le menu complet': 'View full menu',
        'Réserver maintenant': 'Reserve now'
    }
};

const HEURES = {
    midi: ['12:00', '12:15', '12:30', '12:45', '13:00', '13:15'],
    soir: ['19:00', '19:15', '19:30', '19:45', '20:00', '20:15', '20:30']
};

// ========================================
// Utilitaires
// ========================================

/**
 * Fonction de debounce pour limiter les appels de fonction
 * @param {Function} func - Fonction à débouncer
 * @param {number} wait - Délai d'attente en ms
 * @returns {Function}
 */
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Ajoute ou enlève une classe avec animation
 * @param {HTMLElement} element - Élément DOM
 * @param {string} className - Nom de la classe
 * @param {boolean} add - Ajouter (true) ou enlever (false)
 */
const toggleClass = (element, className, add) => {
    if (!element) return;
    
    if (add) {
        element.classList.add(className);
    } else {
        element.classList.remove(className);
    }
};

/**
 * Affiche un message d'erreur pour un champ de formulaire
 * @param {string} fieldName - Nom du champ
 * @param {string} message - Message d'erreur
 */
const showFieldError = (fieldName, message) => {
    const errorElement = document.querySelector(`[data-field="${fieldName}"]`);
    const fieldElement = document.querySelector(`[name="${fieldName}"]`);
    
    if (errorElement) {
        errorElement.textContent = message;
        errorElement.style.display = message ? 'block' : 'none';
    }
    
    if (fieldElement) {
        if (message) {
            fieldElement.classList.add(`${fieldElement.className.split(' ')[0]}--error`);
        } else {
            fieldElement.classList.remove(`${fieldElement.className.split(' ')[0]}--error`);
        }
    }
};

/**
 * Efface tous les messages d'erreur du formulaire
 */
const clearFieldErrors = () => {
    const errorElements = document.querySelectorAll('.form-error');
    errorElements.forEach(element => {
        element.textContent = '';
        element.style.display = 'none';
    });
    
    // Enlever les classes d'erreur de tous les champs
    const formFields = document.querySelectorAll('.form-input, .form-select, .form-textarea');
    formFields.forEach(field => {
        field.classList.remove('form-input--error', 'form-select--error', 'form-textarea--error');
    });
};

// ========================================
// Gestion de la navigation
// ========================================

class Navigation {
    constructor() {
        this.navbar = document.querySelector(SELECTORS.navbar);
        this.toggle = document.querySelector(SELECTORS.navbarToggle);
        this.menu = document.querySelector(SELECTORS.navbarMenu);
        this.isMenuOpen = false;
        
        this.init();
    }
    
    init() {
        this.bindEvents();
        this.setupSmoothScrolling();
    }
    
    bindEvents() {
        // Scroll pour modifier l'apparence de la navbar
        window.addEventListener('scroll', debounce(() => {
            this.handleScroll();
        }, 10));
        
        // Toggle menu mobile
        if (this.toggle) {
            this.toggle.addEventListener('click', () => {
                this.toggleMobileMenu();
            });
        }
        
        // Fermer le menu mobile lors du clic sur un lien
        document.querySelectorAll('.navbar__link').forEach(link => {
            link.addEventListener('click', () => {
                this.closeMobileMenu();
            });
        });
        
        // Fermer le menu mobile lors du clic extérieur
        document.addEventListener('click', (e) => {
            if (this.isMenuOpen && !this.navbar.contains(e.target)) {
                this.closeMobileMenu();
            }
        });
    }
    
    handleScroll() {
        const scrollTop = window.pageYOffset;
        
        if (scrollTop > 100) {
            toggleClass(this.navbar, 'navbar--scrolled', true);
        } else {
            toggleClass(this.navbar, 'navbar--scrolled', false);
        }
    }
    
    toggleMobileMenu() {
        this.isMenuOpen = !this.isMenuOpen;
        toggleClass(this.menu, 'navbar__menu--open', this.isMenuOpen);
        toggleClass(this.toggle, 'navbar__toggle--open', this.isMenuOpen);
        
        // Empêcher le scroll quand le menu est ouvert
        document.body.style.overflow = this.isMenuOpen ? 'hidden' : '';
    }
    
    closeMobileMenu() {
        this.isMenuOpen = false;
        toggleClass(this.menu, 'navbar__menu--open', false);
        toggleClass(this.toggle, 'navbar__toggle--open', false);
        document.body.style.overflow = '';
    }
    
    setupSmoothScrolling() {
        // Scroll indicator du hero
        const scrollIndicator = document.querySelector(SELECTORS.heroScrollIndicator);
        if (scrollIndicator) {
            scrollIndicator.addEventListener('click', () => {
                const firstSection = document.querySelector('.section');
                if (firstSection) {
                    firstSection.scrollIntoView({ behavior: 'smooth' });
                }
            });
        }
        
        // Smooth scroll pour tous les liens d'ancre
        document.querySelectorAll('a[href^="#"]').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const targetId = link.getAttribute('href').substring(1);
                const targetElement = document.getElementById(targetId);
                
                if (targetElement) {
                    targetElement.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            });
        });
    }
}

// ========================================
// Gestion des animations au scroll
// ========================================

class ScrollAnimations {
    constructor() {
        this.elements = document.querySelectorAll(SELECTORS.revealElements);
        this.observer = null;
        
        this.init();
    }
    
    init() {
        this.setupIntersectionObserver();
        this.observeElements();
    }
    
    setupIntersectionObserver() {
        const options = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };
        
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.revealElement(entry.target);
                }
            });
        }, options);
    }
    
    observeElements() {
        this.elements.forEach(element => {
            element.classList.add('reveal');
            this.observer.observe(element);
        });
    }
    
    revealElement(element) {
        element.classList.add('revealed');
        this.observer.unobserve(element);
    }
}

// ========================================
// Gestion des menus et langues
// ========================================

class LanguageManager {
    constructor() {
        this.currentLanguage = 'fr';
        this.init();
    }
    
    init() {
        this.bindLanguageButtons();
        this.updateContent();
    }
    
    bindLanguageButtons() {
        const languageButtons = document.querySelectorAll('.language-btn');
        
        languageButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const lang = button.getAttribute('data-lang');
                this.switchLanguage(lang);
            });
        });
    }
    
    switchLanguage(lang) {
        if (lang === this.currentLanguage) return;
        
        this.currentLanguage = lang;
        
        // Mettre à jour les boutons de langue
        document.querySelectorAll('.language-btn').forEach(btn => {
            btn.classList.remove('language-btn--active');
        });
        
        document.querySelector(`[data-lang="${lang}"]`).classList.add('language-btn--active');
        
        // Mettre à jour le contenu
        this.updateContent();
    }
    
    updateContent() {
        const textElements = document.querySelectorAll('.menu-text');
        
        textElements.forEach(element => {
            const frText = element.getAttribute('data-fr');
            const enText = element.getAttribute('data-en');
            
            if (frText && enText) {
                element.textContent = this.currentLanguage === 'fr' ? frText : enText;
            }
        });
    }
    
    getCurrentLanguage() {
        return this.currentLanguage;
    }
}

class MenuManager {
    constructor() {
        this.languageManager = new LanguageManager();
        this.init();
    }
    
    init() {
        this.bindMenuButtons();
        this.bindPdfDownloadButtons();
    }
    
    bindMenuButtons() {
        const menuButtons = document.querySelectorAll(SELECTORS.menuButtons);
        
        menuButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const menuType = button.getAttribute('data-menu');
                this.openMenu(menuType);
            });
        });
    }
    
    openMenu(menuType) {
        const currentLang = this.languageManager.getCurrentLanguage();
        const url = currentLang === 'fr' ? URLS.menuFr : URLS.menuEn;
        
        // Ouvrir dans un nouvel onglet
        window.open(url, '_blank', 'noopener,noreferrer');
        
        // Animation du bouton
        const button = document.querySelector(`[data-menu="${menuType}"]`);
        if (button) {
            button.style.transform = 'scale(0.95)';
            setTimeout(() => {
                button.style.transform = '';
            }, 150);
        }
    }
    
    bindPdfDownloadButtons() {
        const pdfButtons = document.querySelectorAll('.menu-pdf-download');
        
        pdfButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                this.downloadPdf();
            });
        });
    }
    
    downloadPdf() {
        const currentLang = this.languageManager.getCurrentLanguage();
        const url = currentLang === 'fr' ? URLS.menuFr : URLS.menuEn;
        
        // Créer un lien temporaire pour télécharger le fichier
        const link = document.createElement('a');
        link.href = url;
        link.download = `menu-${currentLang}.html`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}



// ========================================
// Gestion des restrictions de dates
// ========================================

class DateRestrictions {
    constructor() {
        this.closedDays = CONFIG.joursFermes || { weekly: [1], dates: [] };
        this.dateInput = document.querySelector(SELECTORS.dateInput);
        
        this.init();
    }
    
    init() {
        if (this.dateInput) {
            this.setMinDate();
            this.bindEvents();
        }
    }
    
    setMinDate() {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        
        this.dateInput.min = tomorrow.toISOString().split('T')[0];
    }
    
    bindEvents() {
        this.dateInput.addEventListener('change', (e) => {
            this.validateDate(e.target.value);
        });
        
        this.dateInput.addEventListener('input', (e) => {
            this.validateDate(e.target.value);
        });
    }
    
    validateDate(dateString) {
        if (!dateString) return true;
        
        const selectedDate = new Date(dateString + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Vérifier que la date n'est pas dans le passé
        if (selectedDate < today) {
            this.showDateError('Veuillez sélectionner une date future');
            return false;
        }
        
        // Vérifier les jours fermés de la semaine (1 = lundi, 7 = dimanche)
        const dayOfWeek = selectedDate.getDay() === 0 ? 7 : selectedDate.getDay();
        if (this.closedDays.weekly.includes(dayOfWeek)) {
            this.showDateError('Le restaurant est fermé ce jour-là');
            return false;
        }
        
        // Vérifier les dates spécifiques fermées
        const dateStr = selectedDate.toISOString().split('T')[0];
        if (this.closedDays.dates.includes(dateStr)) {
            this.showDateError('Le restaurant est fermé cette date');
            return false;
        }
        
        this.clearDateError();
        return true;
    }
    
    showDateError(message) {
        showFieldError('date', message);
        this.dateInput.setCustomValidity(message);
    }
    
    clearDateError() {
        showFieldError('date', '');
        this.dateInput.setCustomValidity('');
    }
}

// ========================================
// Validation du formulaire
// ========================================

class FormValidator {
    constructor() {
        this.form = document.querySelector(SELECTORS.reservationForm);
        this.periodeSelect = document.querySelector(SELECTORS.periodeSelect);
        this.heureSelect = document.querySelector(SELECTORS.heureSelect);
        
        this.rules = {
            nom: {
                required: true,
                minLength: 2,
                maxLength: 50,
                pattern: /^[a-zA-ZÀ-ÿ\s\-']+$/
            },
            prenom: {
                required: true,
                minLength: 2,
                maxLength: 50,
                pattern: /^[a-zA-ZÀ-ÿ\s\-']+$/
            },
            email: {
                required: false,
                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            },
            personnes: {
                required: true,
                min: 1,
                max: 20
            },
            date: {
                required: true
            },
            periode: {
                required: true
            },
            heure: {
                required: true
            }
        };
        
        this.init();
    }
    
    init() {
        if (!this.form) return;
        
        this.bindEvents();
        this.setupDynamicTimeSlots();
    }
    
    bindEvents() {
        // Validation en temps réel
        this.form.addEventListener('input', (e) => {
            this.validateField(e.target);
        });
        
        this.form.addEventListener('change', (e) => {
            this.validateField(e.target);
        });
        
        // Soumission du formulaire
        this.form.addEventListener('submit', (e) => {
            this.handleSubmit(e);
        });
    }
    
    setupDynamicTimeSlots() {
        if (this.periodeSelect && this.heureSelect) {
            this.periodeSelect.addEventListener('change', () => {
                this.updateTimeSlots();
            });
        }
    }
    
    updateTimeSlots() {
        const selectedPeriode = this.periodeSelect.value;
        const heures = HEURES[selectedPeriode] || [];
        
        // Vider les options existantes
        this.heureSelect.innerHTML = '<option value="">Choisissez un créneau</option>';
        
        if (heures.length > 0) {
            this.heureSelect.disabled = false;
            
            heures.forEach(heure => {
                const option = document.createElement('option');
                option.value = heure;
                option.textContent = heure;
                this.heureSelect.appendChild(option);
            });
        } else {
            this.heureSelect.disabled = true;
        }
        
        // Réinitialiser la validation de l'heure
        this.validateField(this.heureSelect);
    }
    
    validateField(field) {
        if (!field || !field.name) return true;
        
        const fieldName = field.name;
        const value = field.value.trim();
        const rules = this.rules[fieldName];
        
        if (!rules) return true;
        
        // Champ requis
        if (rules.required && !value) {
            showFieldError(fieldName, 'Ce champ est obligatoire');
            return false;
        }
        
        if (value) {
            // Longueur minimale
            if (rules.minLength && value.length < rules.minLength) {
                showFieldError(fieldName, `Minimum ${rules.minLength} caractères`);
                return false;
            }
            
            // Longueur maximale
            if (rules.maxLength && value.length > rules.maxLength) {
                showFieldError(fieldName, `Maximum ${rules.maxLength} caractères`);
                return false;
            }
            
            // Pattern regex
            if (rules.pattern && !rules.pattern.test(value)) {
                switch (fieldName) {
                    case 'nom':
                    case 'prenom':
                        showFieldError(fieldName, 'Seules les lettres, espaces, tirets et apostrophes sont autorisés');
                        break;
                    case 'email':
                        showFieldError(fieldName, 'Format d\'email invalide');
                        break;
                }
                return false;
            }
            
            // Validation numérique
            if (rules.min !== undefined || rules.max !== undefined) {
                const numValue = parseInt(value);
                if (rules.min && numValue < rules.min) {
                    showFieldError(fieldName, `Minimum ${rules.min}`);
                    return false;
                }
                if (rules.max && numValue > rules.max) {
                    showFieldError(fieldName, `Maximum ${rules.max}`);
                    return false;
                }
            }
        }
        
        showFieldError(fieldName, '');
        return true;
    }
    
    validateForm() {
        clearFieldErrors();
        let isValid = true;
        
        // Valider tous les champs avec des règles
        Object.keys(this.rules).forEach(fieldName => {
            const field = this.form.querySelector(`[name="${fieldName}"]`);
            if (field && !this.validateField(field)) {
                isValid = false;
            }
        });
        
        return isValid;
    }
    
    handleSubmit(e) {
        e.preventDefault(); // Empêcher la soumission immédiate
        
        if (!this.validateForm()) {
            // Scroll vers le premier champ en erreur
            const firstError = this.form.querySelector('.form-error:not(:empty)');
            if (firstError) {
                const fieldName = firstError.getAttribute('data-field');
                const field = this.form.querySelector(`[name="${fieldName}"]`);
                if (field) {
                    field.focus();
                    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
            
            return false;
        }
        
        // Ajouter une classe de chargement
        this.form.classList.add('loading');
        
        // Exécuter reCAPTCHA v3 avant soumission
        this.executeRecaptcha();
        
        return false;
    }
    
    executeRecaptcha() {
        console.log('🔐 Tentative d\'exécution reCAPTCHA...');
        console.log('Site Key:', CONFIG.recaptchaSiteKey);
        console.log('grecaptcha disponible:', typeof grecaptcha !== 'undefined');
        
        // Vérifier si reCAPTCHA est configuré
        if (!CONFIG.recaptchaSiteKey || CONFIG.recaptchaSiteKey.trim() === '') {
            console.log('⚠️ Mode développement - reCAPTCHA non configuré, soumission directe');
            this.form.submit();
            return;
        }
        
        if (typeof grecaptcha !== 'undefined' && CONFIG.recaptchaSiteKey) {
            grecaptcha.ready(() => {
                console.log('✅ reCAPTCHA prêt, exécution...');
                grecaptcha.execute(CONFIG.recaptchaSiteKey, { action: 'reservation' }).then((token) => {
                    console.log('✅ Token reCAPTCHA généré:', token.substring(0, 20) + '...');
                    
                    // Injecter le token dans le champ caché
                    const tokenField = document.getElementById('g-recaptcha-response');
                    if (tokenField) {
                        tokenField.value = token;
                        console.log('✅ Token injecté dans le formulaire');
                    }
                    
                    // Soumettre le formulaire
                    console.log('📤 Soumission du formulaire...');
                    this.form.submit();
                }).catch((error) => {
                    console.error('❌ Erreur reCAPTCHA:', error);
                    this.form.classList.remove('loading');
                    
                    // Afficher un message d'erreur
                    showFieldError('recaptcha', 'Erreur de vérification de sécurité. Veuillez réessayer.');
                });
            });
        } else {
            console.warn('⚠️ grecaptcha non disponible - soumission directe');
            // Soumettre quand même si reCAPTCHA n'est pas configuré
            this.form.submit();
        }
    }
}

// ========================================
// Gestionnaire principal de l'application
// ========================================

class RestaurantApp {
    constructor() {
        this.navigation = null;
        this.scrollAnimations = null;
        this.menuManager = null;
        this.dateRestrictions = null;
        this.formValidator = null;
        
        this.init();
    }
    
    init() {
        // Attendre que le DOM soit complètement chargé
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeComponents();
            });
        } else {
            this.initializeComponents();
        }
    }
    
    initializeComponents() {
        try {
            // Initialiser tous les composants
            this.navigation = new Navigation();
            this.scrollAnimations = new ScrollAnimations();
            this.menuManager = new MenuManager();
            this.dateRestrictions = new DateRestrictions();
            this.formValidator = new FormValidator();
            
            console.log('Application initialisée avec succès');
            
        } catch (error) {
            console.error('Erreur lors de l\'initialisation de l\'application:', error);
        }
    }
}

// ========================================
// Initialisation de l'application
// ========================================

// Créer une instance de l'application
window.restaurantApp = new RestaurantApp();

// Export pour les tests (si nécessaire)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        RestaurantApp,
        Navigation,
        ScrollAnimations,
        LanguageManager,
        MenuManager,
        DateRestrictions,
        FormValidator
    };
} 